import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Table, Btn, Badge, Input, Select, Textarea, Modal, Card } from "@/components/UI";
import { Sparkles, Thermometer, ClipboardList, Plus, FileText, Trash2, Bug, Droplets, GraduationCap, X, Pencil, StickyNote, Wrench, Users2, FlaskConical, Shield, AlertTriangle, MessageCircleWarning, GitBranch, ClipboardCheck, AlertOctagon, Recycle, Undo2, BookMarked, Eye } from "lucide-react";
import { GMPAlertsBanner, MaintenanceTab, DocumentsTab, SupplierEvalTab, RetainedSamplesTab } from "./GMPExtended";
import MultiFileDropzone from "@/components/MultiFileDropzone";
import { DeviationsTab, ComplaintsTab, OOSTab } from "./GMPQMS";
import { ChangeControlTab, InternalAuditTab, RiskTab, WasteTab, RecallSummaryTab } from "./GMPQMS2";
import MasterDocuments from "./MasterDocuments";
import { CAPAPanel } from "@/components/CAPAPanel";
import { openPdfPreview } from "@/lib/pdfPreview";
import { todayTR } from "@/lib/date";
import { FasonContractsTab } from "@/components/FasonContractsTab";

const CLEAN_ZONES = ["Üretim Alanı", "Makine ve Teçhizatlar", "Tezgahlar", "Yan Malzemeler", "Personel Hijyeni"];
const TEMP_ZONES = ["Üretim Alanı", "Hammadde Deposu", "Bitmiş Ürün Deposu", "Ambalaj Deposu"];
const EVENT_KINDS = [
  { key: "pest", label: "Haşere Takip ve İlaçlama", icon: Bug, color: "amber" },
  { key: "water", label: "Saf Su Kontrolü", icon: Droplets, color: "cyan" },
  { key: "training", label: "Eğitim Takip", icon: GraduationCap, color: "violet" },
];

const today = () => todayTR();

function downloadPDF(url, filename) {
  // iter146 + iter164 — Content-Type doğrulaması + otomatik PDF Preview dialog.
  //  1) Content-Type doğrulaması (HTML error sayfası .pdf olarak kaydedilmez)
  //  2) JSON error detail parse edilir → kullanıcıya gerçek hata mesajı
  //  3) PDF ise global PDF Preview dialog açılır (openPdfPreview)
  //  4) iOS Safari: openPdfPreview otomatik olarak yeni sekme fallback yapar
  const token = localStorage.getItem("erp_token");
  const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return fetch(fullUrl, { headers: { Authorization: `Bearer ${token}` } })
    .then(async r => {
      if (!r.ok) {
        let msg = `HTTP ${r.status}`;
        try {
          const errBody = await r.clone().json();
          msg = errBody.detail || errBody.message || msg;
        } catch { /* not json — keep HTTP code */ }
        throw new Error(msg);
      }
      const ct = (r.headers.get("content-type") || "").toLowerCase();
      if (!ct.includes("application/pdf")) {
        throw new Error(`Sunucu PDF yerine "${ct || "bilinmeyen"}" döndü — belge oluşturulamadı`);
      }
      return r.arrayBuffer();
    })
    .then(buf => {
      const blob = new Blob([buf], { type: "application/pdf" });
      openPdfPreview(blob, filename);
    })
    .catch(e => {
      alert(`PDF alınamadı: ${e.message}`);
    });
}

/* =================== CLEANING TAB =================== */
function CleaningTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ start: "", end: "", zone: "", status: "" });
  const [busy, setBusy] = useState(false);
  const [pdfMonth, setPdfMonth] = useState(() => today().slice(0, 7));
  // Toplu form: her bölge için status ve not
  const [entries, setEntries] = useState(() => CLEAN_ZONES.map(z => ({ zone: z, status: "", notes: "" })));
  const [noteRow, setNoteRow] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (f.start) params.start = f.start;
      if (f.end) params.end = f.end;
      if (f.zone) params.zone = f.zone;
      if (f.status) params.status = f.status;
      const r = await api.get("/gmp/cleaning", params);
      setRows(r || []);
    } finally { setBusy(false); }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const setEntry = (i, patch) => { const arr = [...entries]; arr[i] = { ...arr[i], ...patch }; setEntries(arr); };
  const saveAll = async () => {
    const toSave = entries.filter(e => e.status === "done" || e.status === "not_done");
    if (toSave.length === 0) { alert("Lütfen en az bir bölge için Yapıldı / Yapılmadı seçin."); return; }
    try {
      const res = await api.post("/gmp/cleaning/bulk", { entries: toSave.map(e => ({ date: today(), zone: e.zone, status: e.status, notes: e.notes || null })) });
      alert(`${res.count} kayıt oluşturuldu${res.capa_created?.length ? ` · ${res.capa_created.length} CAPA talebi otomatik açıldı` : ""}.`);
      setEntries(CLEAN_ZONES.map(z => ({ zone: z, status: "", notes: "" })));
      load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
  };

  const openNote = (r) => { setNoteRow(r); setNoteDraft(r.notes || ""); };
  const saveNote = async () => {
    try { await api.put(`/gmp/cleaning/${noteRow.id}/note`, { notes: noteDraft }); setNoteRow(null); setNoteDraft(""); load(); }
    catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const openEdit = (r) => { setEditRow(r); setEditForm({ date: r.date, zone: r.zone, status: r.status, notes: r.notes || "" }); };
  const saveEdit = async () => {
    try { await api.put(`/gmp/cleaning/${editRow.id}`, editForm); setEditRow(null); load(); }
    catch (e) { alert(e.response?.data?.detail || "Yetkiniz yok"); }
  };
  const del = async (id) => { if (!window.confirm("Kayıt silinsin mi?")) return; await api.delete(`/gmp/cleaning/${id}`); load(); };
  const exportPdf = () => { const [y, m] = pdfMonth.split("-"); downloadPDF(`/api/gmp/cleaning/pdf?year=${y}&month=${parseInt(m, 10)}`, `gmp_temizlik_${y}_${m}.pdf`); };

  return (
    <div>
      {/* Toplu kayıt paneli */}
      <Card className="p-3 sm:p-4 mb-4 border-l-4 border-emerald-500 bg-emerald-50/30">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
          <div>
            <div className="text-sm font-semibold text-emerald-900">Günlük Temizlik Kaydı</div>
            <div className="text-xs text-slate-600">Tarih, saat ve kayıt eden otomatik alınır — {today()} · {user?.name || "—"}</div>
          </div>
          <Btn onClick={saveAll} data-testid="clean-save-all" size="lg" variant="approve" className="w-full sm:w-auto">Kaydet</Btn>
        </div>
        {/* iter145 — Mobil: kart görünümü (sm altı); Desktop/Tablet: mevcut tablo AYNI */}
        <div className="space-y-2 sm:hidden" data-testid="clean-bulk-cards-mobile">
          {entries.map((e, i) => (
            <div key={e.zone} className="border border-slate-200 rounded-lg p-3 bg-white">
              <div className="font-semibold text-sm mb-2">{e.zone}</div>
              <div className="flex gap-2 mb-2">
                <button onClick={() => setEntry(i, { status: e.status === "done" ? "" : "done" })} data-testid={`clean-mobile-done-${i}`}
                  className={`flex-1 min-h-[44px] px-3 py-2 text-sm font-semibold rounded border-2 ${e.status === "done" ? "bg-emerald-500 border-emerald-600 text-white" : "bg-white border-slate-300 text-slate-500"}`}>
                  ✓ Yapıldı
                </button>
                <button onClick={() => setEntry(i, { status: e.status === "not_done" ? "" : "not_done" })} data-testid={`clean-mobile-notdone-${i}`}
                  className={`flex-1 min-h-[44px] px-3 py-2 text-sm font-semibold rounded border-2 ${e.status === "not_done" ? "bg-red-500 border-red-600 text-white" : "bg-white border-slate-300 text-slate-500"}`}>
                  ✗ Yapılmadı
                </button>
              </div>
              <input value={e.notes} onChange={ev => setEntry(i, { notes: ev.target.value })} placeholder="Kısa not..." className="w-full px-3 py-2 text-sm border rounded"/>
            </div>
          ))}
        </div>
        <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-xs">
            <tr><th className="text-left px-3 py-2">Bölge</th><th className="px-3 py-2 w-64">Durum</th><th className="text-left px-3 py-2">Not (opsiyonel)</th></tr>
          </thead>
          <tbody>
            {entries.map((e, i) => (
              <tr key={e.zone} className="border-t border-slate-200">
                <td className="px-3 py-2 font-medium">{e.zone}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-2 justify-center">
                    <button onClick={() => setEntry(i, { status: e.status === "done" ? "" : "done" })} data-testid={`clean-row-done-${i}`}
                      className={`px-3 py-1.5 text-xs font-semibold rounded border-2 ${e.status === "done" ? "bg-emerald-500 border-emerald-600 text-white" : "bg-white border-slate-300 text-slate-500 hover:border-emerald-400"}`}>
                      ✓ Yapıldı
                    </button>
                    <button onClick={() => setEntry(i, { status: e.status === "not_done" ? "" : "not_done" })} data-testid={`clean-row-notdone-${i}`}
                      className={`px-3 py-1.5 text-xs font-semibold rounded border-2 ${e.status === "not_done" ? "bg-red-500 border-red-600 text-white" : "bg-white border-slate-300 text-slate-500 hover:border-red-400"}`}>
                      ✗ Yapılmadı
                    </button>
                  </div>
                </td>
                <td className="px-3 py-2">
                  <input value={e.notes} onChange={ev => setEntry(i, { notes: ev.target.value })} placeholder="Kısa not..." className="w-full px-2 py-1 text-xs border rounded" data-testid={`clean-row-note-${i}`}/>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>

      <div className="flex flex-wrap items-end gap-2 mb-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
        <Input label="Başlangıç" type="date" value={f.start} onChange={e => setF({ ...f, start: e.target.value })} data-testid="clean-filter-start"/>
        <Input label="Bitiş" type="date" value={f.end} onChange={e => setF({ ...f, end: e.target.value })} data-testid="clean-filter-end"/>
        <Select label="Bölge" value={f.zone} onChange={e => setF({ ...f, zone: e.target.value })}>
          <option value="">Tümü</option>{CLEAN_ZONES.map(z => <option key={z} value={z}>{z}</option>)}
        </Select>
        <Select label="Durum" value={f.status} onChange={e => setF({ ...f, status: e.target.value })}>
          <option value="">Tümü</option><option value="done">Yapıldı</option><option value="not_done">Yapılmadı</option>
        </Select>
        <div className="flex-1"/>
        <Input label="Ay (PDF)" type="month" value={pdfMonth} onChange={e => setPdfMonth(e.target.value)}/>
        <Btn variant="secondary" onClick={exportPdf} data-testid="clean-pdf-btn" title="Aylık Temizlik PDF"><FileText size={14}/></Btn>
      </div>
      <Table testid="clean-table" rows={rows} columns={[
        { label: "Tarih", render: r => <span className="font-mono text-xs">{r.date}</span> },
        { label: "Saat", render: r => <span className="font-mono text-xs">{r.time || "—"}</span> },
        { label: "Bölge", key: "zone" },
        { label: "Durum", render: r => <Badge level={r.status === "done" ? "ok" : "attention"}>{r.status === "done" ? "✓ Yapıldı" : "✗ Yapılmadı"}</Badge> },
        { label: "Not", render: r => <span className="text-xs text-slate-600 max-w-[220px] block truncate" title={r.notes}>{r.notes || "—"}</span> },
        { label: "Kayıt Eden", key: "created_by_name" },
        { label: "İşlem", render: r => (
          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
            <Btn size="sm" variant="secondary" onClick={() => openNote(r)} data-testid={`clean-note-${r.id}`}><StickyNote size={12}/> Not</Btn>
            {isAdmin && <Btn size="sm" variant="secondary" onClick={() => openEdit(r)} data-testid={`clean-edit-${r.id}`}><Pencil size={12}/> Düzenle</Btn>}
            {isAdmin && <Btn size="sm" variant="danger" onClick={() => del(r.id)} data-testid={`clean-del-${r.id}`}><Trash2 size={12}/></Btn>}
          </div>
        ) },
      ]} />
      {!busy && rows.length === 0 && <div className="text-center py-6 text-slate-400 text-sm">Filtreye uygun kayıt yok.</div>}

      <Modal open={!!noteRow} onClose={() => setNoteRow(null)} title={`Not — ${noteRow?.zone || ""}`} size="md">
        <Textarea label="Not" value={noteDraft} onChange={e => setNoteDraft(e.target.value)} rows={4} data-testid="clean-note-input"/>
        <div className="flex justify-end gap-2 mt-3"><Btn variant="secondary" onClick={() => setNoteRow(null)}>Vazgeç</Btn><Btn onClick={saveNote} data-testid="clean-note-save">Kaydet</Btn></div>
      </Modal>
      <Modal open={!!editRow} onClose={() => setEditRow(null)} title="Kayıt Düzenle (Sistem Yetkilisi)" size="md">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Tarih" type="date" value={editForm.date || ""} onChange={e => setEditForm({ ...editForm, date: e.target.value })} data-testid="clean-edit-date"/>
          <Select label="Bölge" value={editForm.zone || ""} onChange={e => setEditForm({ ...editForm, zone: e.target.value })}>
            {CLEAN_ZONES.map(z => <option key={z} value={z}>{z}</option>)}
          </Select>
          <Select label="Durum" value={editForm.status || ""} onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
            <option value="done">Yapıldı</option><option value="not_done">Yapılmadı</option>
          </Select>
          <div className="col-span-2"><Textarea label="Not" value={editForm.notes || ""} onChange={e => setEditForm({ ...editForm, notes: e.target.value })}/></div>
        </div>
        <div className="flex justify-end gap-2 mt-3"><Btn variant="secondary" onClick={() => setEditRow(null)}>Vazgeç</Btn><Btn onClick={saveEdit} data-testid="clean-edit-save">Güncelle</Btn></div>
      </Modal>
    </div>
  );
}

/* =================== TEMP/HUMIDITY TAB =================== */
function TempHumidityTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ start: "", end: "", zone: "" });
  const [pdfMonth, setPdfMonth] = useState(() => today().slice(0, 7));
  const [entries, setEntries] = useState(() => TEMP_ZONES.map(z => ({ zone: z, temperature_c: "", humidity_pct: "", notes: "" })));
  const [noteRow, setNoteRow] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = useCallback(async () => {
    const params = {};
    if (f.start) params.start = f.start;
    if (f.end) params.end = f.end;
    if (f.zone) params.zone = f.zone;
    const r = await api.get("/gmp/temp-humidity", params);
    setRows(r || []);
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const setEntry = (i, patch) => { const arr = [...entries]; arr[i] = { ...arr[i], ...patch }; setEntries(arr); };
  const saveAll = async () => {
    const toSave = entries.filter(e => e.temperature_c !== "" || e.humidity_pct !== "");
    if (toSave.length === 0) { alert("Lütfen en az bir bölge için değer girin."); return; }
    try {
      const res = await api.post("/gmp/temp-humidity/bulk", { entries: toSave.map(e => ({ date: today(), zone: e.zone, temperature_c: parseFloat(e.temperature_c) || 0, humidity_pct: parseFloat(e.humidity_pct) || 0, notes: e.notes || null })) });
      alert(`${res.count} ölçüm kaydedildi${res.capa_created?.length ? ` · ${res.capa_created.length} CAPA talebi otomatik açıldı (sınır dışı)` : ""}.`);
      setEntries(TEMP_ZONES.map(z => ({ zone: z, temperature_c: "", humidity_pct: "", notes: "" })));
      load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
  };

  const openNote = (r) => { setNoteRow(r); setNoteDraft(r.notes || ""); };
  const saveNote = async () => { await api.put(`/gmp/temp-humidity/${noteRow.id}/note`, { notes: noteDraft }); setNoteRow(null); setNoteDraft(""); load(); };
  const openEdit = (r) => { setEditRow(r); setEditForm({ date: r.date, time: r.time, zone: r.zone, temperature_c: r.temperature_c, humidity_pct: r.humidity_pct, notes: r.notes || "" }); };
  const saveEdit = async () => {
    try { await api.put(`/gmp/temp-humidity/${editRow.id}`, { ...editForm, temperature_c: parseFloat(editForm.temperature_c) || 0, humidity_pct: parseFloat(editForm.humidity_pct) || 0 }); setEditRow(null); load(); }
    catch (e) { alert(e.response?.data?.detail || "Yetkiniz yok"); }
  };
  const del = async (id) => { if (!window.confirm("Kayıt silinsin mi?")) return; await api.delete(`/gmp/temp-humidity/${id}`); load(); };
  const exportPdf = () => { const [y, m] = pdfMonth.split("-"); downloadPDF(`/api/gmp/temp-humidity/pdf?year=${y}&month=${parseInt(m, 10)}`, `gmp_sicaklik_nem_${y}_${m}.pdf`); };

  return (
    <div>
      <Card className="p-3 sm:p-4 mb-4 border-l-4 border-cyan-500 bg-cyan-50/30">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
          <div>
            <div className="text-sm font-semibold text-cyan-900">Günlük Sıcaklık ve Nem Ölçümü</div>
            <div className="text-xs text-slate-600">Tarih, saat ve ölçüm alan kişi otomatik alınır — {today()} · {user?.name || "—"} · <span className="text-slate-500">(Sınır: 15-30°C / 20-70% nem)</span></div>
          </div>
          <Btn onClick={saveAll} data-testid="temp-save-all" size="lg" variant="approve" className="w-full sm:w-auto">Kaydet</Btn>
        </div>
        {/* iter145 — Mobil: kart görünümü; Desktop/Tablet: mevcut tablo AYNI */}
        <div className="space-y-2 sm:hidden" data-testid="temp-bulk-cards-mobile">
          {entries.map((e, i) => (
            <div key={e.zone} className="border border-slate-200 rounded-lg p-3 bg-white">
              <div className="font-semibold text-sm mb-2">{e.zone}</div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div>
                  <label className="text-[10px] uppercase text-slate-500 font-semibold">Sıcaklık (°C)</label>
                  <input type="number" inputMode="decimal" step="0.1" value={e.temperature_c} onChange={ev => setEntry(i, { temperature_c: ev.target.value })} placeholder="22.5" className="w-full min-h-[44px] px-3 py-2 text-base border rounded text-center font-mono" data-testid={`temp-mobile-t-${i}`}/>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-slate-500 font-semibold">Nem (%)</label>
                  <input type="number" inputMode="decimal" step="0.1" value={e.humidity_pct} onChange={ev => setEntry(i, { humidity_pct: ev.target.value })} placeholder="48.0" className="w-full min-h-[44px] px-3 py-2 text-base border rounded text-center font-mono" data-testid={`temp-mobile-h-${i}`}/>
                </div>
              </div>
              <input value={e.notes} onChange={ev => setEntry(i, { notes: ev.target.value })} placeholder="Kısa not..." className="w-full px-3 py-2 text-sm border rounded"/>
            </div>
          ))}
        </div>
        <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-xs">
            <tr>
              <th className="text-left px-3 py-2">Bölge</th>
              <th className="px-3 py-2 w-32">Sıcaklık (°C)</th>
              <th className="px-3 py-2 w-32">Nem (%)</th>
              <th className="text-left px-3 py-2">Not (opsiyonel)</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => (
              <tr key={e.zone} className="border-t border-slate-200">
                <td className="px-3 py-2 font-medium">{e.zone}</td>
                <td className="px-3 py-2">
                  <input type="number" step="0.1" value={e.temperature_c} onChange={ev => setEntry(i, { temperature_c: ev.target.value })} placeholder="22.5" className="w-full px-2 py-1 text-xs border rounded text-center font-mono" data-testid={`temp-row-t-${i}`}/>
                </td>
                <td className="px-3 py-2">
                  <input type="number" step="0.1" value={e.humidity_pct} onChange={ev => setEntry(i, { humidity_pct: ev.target.value })} placeholder="48.0" className="w-full px-2 py-1 text-xs border rounded text-center font-mono" data-testid={`temp-row-h-${i}`}/>
                </td>
                <td className="px-3 py-2">
                  <input value={e.notes} onChange={ev => setEntry(i, { notes: ev.target.value })} placeholder="Kısa not..." className="w-full px-2 py-1 text-xs border rounded" data-testid={`temp-row-note-${i}`}/>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>

      <div className="flex flex-wrap items-end gap-2 mb-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
        <Input label="Başlangıç" type="date" value={f.start} onChange={e => setF({ ...f, start: e.target.value })}/>
        <Input label="Bitiş" type="date" value={f.end} onChange={e => setF({ ...f, end: e.target.value })}/>
        <Select label="Bölge" value={f.zone} onChange={e => setF({ ...f, zone: e.target.value })}>
          <option value="">Tümü</option>{TEMP_ZONES.map(z => <option key={z} value={z}>{z}</option>)}
        </Select>
        <div className="flex-1"/>
        <Input label="Ay (PDF)" type="month" value={pdfMonth} onChange={e => setPdfMonth(e.target.value)}/>
        <Btn variant="secondary" onClick={exportPdf} data-testid="temp-pdf-btn" title="Aylık Sıcaklık/Nem PDF"><FileText size={14}/></Btn>
      </div>
      <Table testid="temp-table" rows={rows} columns={[
        { label: "Tarih", render: r => <span className="font-mono text-xs">{r.date}</span> },
        { label: "Saat", render: r => <span className="font-mono text-xs">{r.time || "—"}</span> },
        { label: "Bölge", key: "zone" },
        { label: "Sıcaklık (°C)", render: r => {
          const out = r.temperature_c < 15 || r.temperature_c > 30;
          return <span className={`font-mono ${out ? "text-red-600 font-bold" : ""}`}>{r.temperature_c?.toFixed(1)}{out && " ⚠"}</span>;
        } },
        { label: "Nem (%)", render: r => {
          const out = r.humidity_pct < 20 || r.humidity_pct > 70;
          return <span className={`font-mono ${out ? "text-red-600 font-bold" : ""}`}>{r.humidity_pct?.toFixed(1)}{out && " ⚠"}</span>;
        } },
        { label: "Not", render: r => <span className="text-xs text-slate-600 max-w-[180px] block truncate" title={r.notes}>{r.notes || "—"}</span> },
        { label: "Kayıt Eden", key: "created_by_name" },
        { label: "İşlem", render: r => (
          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
            <Btn size="sm" variant="secondary" onClick={() => openNote(r)} data-testid={`temp-note-${r.id}`}><StickyNote size={12}/> Not</Btn>
            {isAdmin && <Btn size="sm" variant="secondary" onClick={() => openEdit(r)} data-testid={`temp-edit-${r.id}`}><Pencil size={12}/> Düzenle</Btn>}
            {isAdmin && <Btn size="sm" variant="danger" onClick={() => del(r.id)} data-testid={`temp-del-${r.id}`}><Trash2 size={12}/></Btn>}
          </div>
        ) },
      ]} />
      {rows.length === 0 && <div className="text-center py-6 text-slate-400 text-sm">Ölçüm kaydı yok.</div>}

      <Modal open={!!noteRow} onClose={() => setNoteRow(null)} title={`Not — ${noteRow?.zone || ""}`}>
        <Textarea label="Not" value={noteDraft} onChange={e => setNoteDraft(e.target.value)} rows={4}/>
        <div className="flex justify-end gap-2 mt-3"><Btn variant="secondary" onClick={() => setNoteRow(null)}>Vazgeç</Btn><Btn onClick={saveNote}>Kaydet</Btn></div>
      </Modal>
      <Modal open={!!editRow} onClose={() => setEditRow(null)} title="Ölçüm Düzenle (Sistem Yetkilisi)">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Tarih" type="date" value={editForm.date || ""} onChange={e => setEditForm({ ...editForm, date: e.target.value })}/>
          <Input label="Saat" type="time" value={editForm.time || ""} onChange={e => setEditForm({ ...editForm, time: e.target.value })}/>
          <Select label="Bölge" value={editForm.zone || ""} onChange={e => setEditForm({ ...editForm, zone: e.target.value })}>
            {TEMP_ZONES.map(z => <option key={z} value={z}>{z}</option>)}
          </Select>
          <div/>
          <Input label="Sıcaklık (°C)" type="number" step="0.1" value={editForm.temperature_c ?? ""} onChange={e => setEditForm({ ...editForm, temperature_c: e.target.value })}/>
          <Input label="Nem (%)" type="number" step="0.1" value={editForm.humidity_pct ?? ""} onChange={e => setEditForm({ ...editForm, humidity_pct: e.target.value })}/>
          <div className="col-span-2"><Textarea label="Not" value={editForm.notes || ""} onChange={e => setEditForm({ ...editForm, notes: e.target.value })}/></div>
        </div>
        <div className="flex justify-end gap-2 mt-3"><Btn variant="secondary" onClick={() => setEditRow(null)}>Vazgeç</Btn><Btn onClick={saveEdit}>Güncelle</Btn></div>
      </Modal>
    </div>
  );
}

/* =================== GENERAL EVENTS TAB =================== */
function GeneralTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // iter310 — GMP yetki modeli (iter304): normal GMP user CREATE; admin/sysauth UPDATE/DELETE.
  const canCreate = !!user && (isAdmin || (user?.module_permissions?.gmp !== false));
  const canEditDelete = !!user && (isAdmin || !!user?.system_authority);
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [f, setF] = useState({ kind: "", start: "", end: "" });
  const [open, setOpen] = useState(null); // "pest" | "water" | "training" | null
  const [editOpen, setEditOpen] = useState(null); // existing record being edited
  const [detail, setDetail] = useState(null); // iter310 — detail drawer for a selected record
  const emptyPest = { date: today(), company: "", doc_no: "", treatment_date: today(), location: "", next_due: "", notes: "", files: [] };
  const emptyWater = { date: today(), tds: "", ph: "", odor: "", appearance: "", extras: [], notes: "" };
  const emptyTrain = { date: today(), training_name: "", staff_name: "", trainer: "", signature: "", next_due: "", notes: "", participant_ids: [], files: [] };
  const [pest, setPest] = useState(emptyPest);
  const [water, setWater] = useState(emptyWater);
  const [train, setTrain] = useState(emptyTrain);

  const load = useCallback(async () => {
    const params = {};
    if (f.kind) params.kind = f.kind;
    if (f.start) params.start = f.start;
    if (f.end) params.end = f.end;
    const r = await api.get("/gmp/general", params);
    setRows(r || []);
    setDetail(prev => prev ? (r.find(x => x.id === prev.id) || prev) : prev);
  }, [f]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // iter310 — Katılımcı seçimi için personel listesini çek (training için).
    api.get("/personnel/employees").then(e => setEmployees(e || [])).catch(() => {});
  }, []);

  // iter311 — Toplu upload: aynı anda birden fazla dosya ekleme destekli yardımcılar.
  const addFilesTo = (which, newFiles) => {
    const arr = Array.isArray(newFiles) ? newFiles : [newFiles];
    if (arr.length === 0) return;
    if (which === "pest") setPest(p => ({ ...p, files: [...(p.files || []), ...arr] }));
    else if (which === "train") setTrain(p => ({ ...p, files: [...(p.files || []), ...arr] }));
    else if (which === "edit") setEditOpen(p => ({ ...p, payload: { ...p.payload, files: [...(p.payload?.files || []), ...arr] } }));
  };
  const removeFileFrom = (which, idx) => {
    if (which === "pest") setPest(p => ({ ...p, files: (p.files || []).filter((_, i) => i !== idx) }));
    else if (which === "train") setTrain(p => ({ ...p, files: (p.files || []).filter((_, i) => i !== idx) }));
    else if (which === "edit") setEditOpen(p => ({ ...p, payload: { ...p.payload, files: (p.payload?.files || []).filter((_, i) => i !== idx) } }));
  };
  const dlFile = (eid, idx, name) => {
    const token = localStorage.getItem("erp_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gmp/general/${eid}/file/${idx}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.blob()).then(b => {
      const u = URL.createObjectURL(b);
      const a = document.createElement("a"); a.href = u; a.download = name; a.click();
    });
  };

  const savePest = async () => {
    if (!pest.company.trim()) { alert("İlaçlama firma adı zorunlu"); return; }
    await api.post("/gmp/general", { kind: "pest", date: pest.date, payload: { company: pest.company, doc_no: pest.doc_no, treatment_date: pest.treatment_date, location: pest.location, next_due: pest.next_due || null, notes: pest.notes, files: pest.files || [] } });
    setPest(emptyPest); setOpen(null); load();
  };
  const saveWater = async () => {
    await api.post("/gmp/general", { kind: "water", date: water.date, payload: { tds: water.tds, ph: water.ph, odor: water.odor, appearance: water.appearance, extras: water.extras, notes: water.notes } });
    setWater(emptyWater); setOpen(null); load();
  };
  const saveTrain = async () => {
    if (!train.training_name.trim()) { alert("Eğitim adı zorunlu"); return; }
    await api.post("/gmp/general", { kind: "training", date: train.date, payload: { training_name: train.training_name, staff_name: train.staff_name, trainer: train.trainer, signature: train.signature, next_due: train.next_due || null, notes: train.notes, participant_ids: train.participant_ids || [], files: train.files || [] } });
    setTrain(emptyTrain); setOpen(null); load();
  };
  const saveEdit = async () => {
    try {
      await api.put(`/gmp/general/${editOpen.id}`, { date: editOpen.date, payload: editOpen.payload });
      setEditOpen(null); load();
    } catch (e) { alert(e.response?.data?.detail || "Güncelleme başarısız"); }
  };
  const del = async (id) => {
    if (!window.confirm("Kayıt silinsin mi?")) return;
    try { await api.delete(`/gmp/general/${id}`); if (detail?.id === id) setDetail(null); load(); }
    catch (e) { alert(e.response?.data?.detail || "Silme başarısız"); }
  };
  const exportPdf = () => {
    const qs = new URLSearchParams();
    if (f.kind) qs.set("kind", f.kind);
    if (f.start) qs.set("start", f.start);
    if (f.end) qs.set("end", f.end);
    downloadPDF(`/api/gmp/general/pdf?${qs.toString()}`, `gmp_genel_${f.kind || "tumu"}_${f.start || ""}_${f.end || ""}.pdf`);
  };

  // iter310 — Row title + history grouping key.
  const titleOf = (r) => {
    const p = r.payload || {};
    if (r.kind === "pest") return p.company || "Haşere Kaydı";
    if (r.kind === "training") return p.training_name || "Eğitim Kaydı";
    if (r.kind === "water") return "Saf Su — " + (r.date || "");
    return "Kayıt";
  };
  const historyFor = (r) => {
    const p = r.payload || {};
    if (r.kind === "pest") {
      const key = (p.company || "").trim().toLowerCase();
      return rows.filter(x => x.kind === "pest" && ((x.payload?.company || "").trim().toLowerCase() === key));
    }
    if (r.kind === "training") {
      const key = (p.training_name || "").trim().toLowerCase();
      return rows.filter(x => x.kind === "training" && ((x.payload?.training_name || "").trim().toLowerCase() === key));
    }
    return [r];
  };
  const dueBadge = (iso) => {
    if (!iso) return <span className="text-slate-400 text-xs">—</span>;
    const d = Math.floor((new Date(iso) - new Date()) / 86400000);
    const cls = d < 0 ? "bg-red-100 text-red-800" : d <= 30 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800";
    const lbl = d < 0 ? `${-d} gün geçti` : `${d} gün`;
    return <span className={`text-xs px-2 py-0.5 rounded font-mono ${cls}`}>{iso} ({lbl})</span>;
  };

  const renderPayload = (r) => {
    const p = r.payload || {};
    if (r.kind === "pest") return <span className="text-xs">Firma: <b>{p.company}</b> · Belge: {p.doc_no || "—"} · İlaçlama: {p.treatment_date || "—"}{p.next_due ? ` · Sonraki: ${p.next_due}` : ""}{(p.files||[]).length ? ` · 📎${p.files.length}` : ""}</span>;
    if (r.kind === "water") {
      const extras = (p.extras || []).map(x => `${x.name}:${x.value}`).join(", ");
      return <span className="text-xs">TDS: <b>{p.tds || "—"}</b> · pH: <b>{p.ph || "—"}</b> · Koku: {p.odor || "—"} · Görünüm: {p.appearance || "—"}{extras ? ` · ${extras}` : ""}</span>;
    }
    if (r.kind === "training") return <span className="text-xs">Eğitim: <b>{p.training_name}</b> · Veren: {p.trainer || "—"}{(p.participant_ids||[]).length ? ` · 👥${p.participant_ids.length}` : ""}{p.next_due ? ` · Tekrar: ${p.next_due}` : ""}{(p.files||[]).length ? ` · 📎${p.files.length}` : ""}</span>;
    return "—";
  };
  const kindBadge = (k) => {
    const e = EVENT_KINDS.find(x => x.key === k);
    if (!e) return k;
    const Icon = e.icon;
    return <span className={`inline-flex items-center gap-1 text-xs bg-${e.color}-50 text-${e.color}-800 border border-${e.color}-200 px-2 py-0.5 rounded`}><Icon size={12}/> {e.label}</span>;
  };

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        {EVENT_KINDS.map(e => {
          const Icon = e.icon;
          const disabled = !canCreate;
          return (
            <Card key={e.key} className={`p-3 ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:shadow-md"} transition border-l-4 border-${e.color}-500`} onClick={() => !disabled && setOpen(e.key)} data-testid={`gen-new-${e.key}`}>
              <div className="flex items-center gap-2">
                <div className={`p-2 rounded-lg bg-${e.color}-50 text-${e.color}-700`}><Icon size={18}/></div>
                <div>
                  <div className="text-sm font-semibold">{e.label}</div>
                  <div className="text-xs text-slate-500">+ Yeni Kayıt Ekle</div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      <div className="flex flex-wrap items-end gap-2 mb-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
        <Select label="Olay Tipi" value={f.kind} onChange={e => setF({ ...f, kind: e.target.value })} data-testid="gen-filter-kind">
          <option value="">Tümü</option>
          {EVENT_KINDS.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
        </Select>
        <Input label="Başlangıç" type="date" value={f.start} onChange={e => setF({ ...f, start: e.target.value })}/>
        <Input label="Bitiş" type="date" value={f.end} onChange={e => setF({ ...f, end: e.target.value })}/>
        <div className="flex-1"/>
        <Btn variant="secondary" onClick={exportPdf} data-testid="gen-pdf-btn" title={f.kind ? "Filtreli Genel Takip PDF" : "Tümü Genel Takip PDF"}><FileText size={14}/></Btn>
      </div>
      <Table testid="gen-table" rows={rows} columns={[
        { label: "Tarih", render: r => <span className="font-mono text-xs">{r.date}</span> },
        { label: "Olay", render: r => kindBadge(r.kind) },
        { label: "Başlık", render: r => (
          <button onClick={() => setDetail(r)} className="text-indigo-700 hover:underline font-semibold text-left text-sm" data-testid={`gen-title-${r.id}`}>
            {titleOf(r)}
          </button>
        ) },
        { label: "Detay", render: renderPayload },
        { label: "Kayıt Eden", key: "created_by_name" },
        { label: "İşlem", render: r => (
          <div className="flex gap-1">
            <Btn size="sm" variant="secondary" onClick={() => setDetail(r)} data-testid={`gen-view-${r.id}`}><Eye size={10}/></Btn>
            {canEditDelete && <Btn size="sm" variant="danger" onClick={() => del(r.id)} data-testid={`gen-del-${r.id}`}><Trash2 size={12}/></Btn>}
          </div>
        ) },
      ]} />
      {rows.length === 0 && <div className="text-center py-8 text-slate-400 text-sm">Kayıt yok.</div>}

      {/* iter310 — Detail drawer */}
      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail ? `${EVENT_KINDS.find(x => x.key === detail.kind)?.label || detail.kind} — ${titleOf(detail)}` : ""} size="full">
        {detail && (() => {
          const p = detail.payload || {};
          const hist = historyFor(detail);
          const participants = (p.participant_ids || []).map(id => employees.find(e => e.id === id)).filter(Boolean);
          return (
            <div className="space-y-4" data-testid="gen-detail-drawer">
              <Card className="p-4 bg-slate-50">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <div><div className="text-slate-500">Kayıt Tarihi</div><div className="font-mono font-bold">{detail.date}</div></div>
                  {detail.kind === "pest" && <>
                    <div><div className="text-slate-500">İlaçlama Firma</div><div className="font-semibold">{p.company || "—"}</div></div>
                    <div><div className="text-slate-500">Belge No</div><div>{p.doc_no || "—"}</div></div>
                    <div><div className="text-slate-500">İlaçlama Tarihi</div><div>{p.treatment_date || "—"}</div></div>
                    <div><div className="text-slate-500">Lokasyon</div><div>{p.location || "—"}</div></div>
                    <div><div className="text-slate-500">Sonraki Uygulama</div><div data-testid="gen-detail-next-due">{dueBadge(p.next_due)}</div></div>
                  </>}
                  {detail.kind === "training" && <>
                    <div><div className="text-slate-500">Eğitim Adı</div><div className="font-semibold">{p.training_name}</div></div>
                    <div><div className="text-slate-500">Eğitim Veren</div><div>{p.trainer || "—"}</div></div>
                    <div><div className="text-slate-500">Personel (metin)</div><div>{p.staff_name || "—"}</div></div>
                    <div><div className="text-slate-500">Tekrar Eğitim</div><div data-testid="gen-detail-next-due">{dueBadge(p.next_due)}</div></div>
                  </>}
                  {detail.kind === "water" && <>
                    <div><div className="text-slate-500">TDS</div><div>{p.tds || "—"}</div></div>
                    <div><div className="text-slate-500">pH</div><div>{p.ph || "—"}</div></div>
                    <div><div className="text-slate-500">Koku</div><div>{p.odor || "—"}</div></div>
                    <div><div className="text-slate-500">Görünüm</div><div>{p.appearance || "—"}</div></div>
                  </>}
                  <div className="col-span-full"><div className="text-slate-500">Not</div><div className="text-slate-700">{p.notes || "—"}</div></div>
                  <div><div className="text-slate-500">Kayıt Eden</div><div className="font-semibold">{detail.created_by_name}</div><div className="text-slate-400">{(detail.created_at||"").slice(0,10)}</div></div>
                  {detail.updated_by_name && <div><div className="text-slate-500">Son Düzenleme</div><div className="font-semibold">{detail.updated_by_name}</div><div className="text-slate-400">{(detail.updated_at||"").slice(0,10)}</div></div>}
                </div>
                {canEditDelete && (
                  <div className="flex gap-2 mt-3">
                    <Btn size="sm" variant="secondary" onClick={() => setEditOpen({ ...detail })} data-testid="gen-detail-edit"><FileText size={12}/> Düzenle</Btn>
                    <Btn size="sm" variant="danger" onClick={() => del(detail.id)} data-testid="gen-detail-del"><Trash2 size={12}/> Sil</Btn>
                  </div>
                )}
              </Card>

              {detail.kind === "training" && participants.length > 0 && (
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-bold">Katılımcılar ({participants.length})</div>
                    <Btn
                      size="sm"
                      variant="secondary"
                      onClick={() => downloadPDF(`/api/gmp/general/${detail.id}/participation-form.pdf`, `Egitim_Katilim_Formu_${(detail.payload?.training_name || 'egitim').replace(/\s+/g, '_')}.pdf`)}
                      data-testid="gen-detail-participation-pdf"
                      title="Katılımcılar + ıslak imza alanları ile matbu PDF indir"
                    >
                      <FileText size={12}/> Katılım Formu (PDF)
                    </Btn>
                  </div>
                  <div className="flex flex-wrap gap-2" data-testid="gen-detail-participants">
                    {participants.map(e => (
                      <span key={e.id} className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-800 border border-indigo-200 px-2 py-1 rounded">
                        {e.full_name || e.name || e.id}
                      </span>
                    ))}
                  </div>
                </Card>
              )}

              <Card className="p-4">
                <div className="text-sm font-bold mb-2">Belgeler ({(p.files || []).length})</div>
                {(p.files || []).length === 0 ? (
                  <div className="text-xs text-slate-400 italic">Henüz belge yüklenmemiş.</div>
                ) : (
                  <div className="flex flex-col gap-1" data-testid="gen-detail-files">
                    {p.files.map((f, i) => (
                      <button key={i} onClick={() => dlFile(detail.id, i, f.name)} className="text-xs text-indigo-700 hover:underline text-left flex items-center gap-1" data-testid={`gen-detail-file-${i}`}>
                        <FileText size={12}/> {f.name} <span className="text-slate-400">({Math.round((f.size||0)/1024)} KB)</span>
                      </button>
                    ))}
                  </div>
                )}
              </Card>

              {(detail.kind === "pest" || detail.kind === "training") && (
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-bold">Geçmiş ({hist.length})</div>
                    <div className="text-xs text-slate-500">Aynı {detail.kind === "pest" ? "firma" : "eğitim konusu"} için tüm kayıtlar</div>
                  </div>
                  <div className="max-h-[400px] overflow-auto">
                    <Table testid="gen-detail-history" rows={hist} columns={[
                      { label: "Tarih", render: r => <span className="font-mono">{r.date}</span> },
                      { label: "Detay", render: renderPayload },
                      { label: "Sonraki", render: r => dueBadge(r.payload?.next_due) },
                      { label: "Kayıt", render: r => <span className="text-[11px]">{r.created_by_name}<br/><span className="text-slate-400">{(r.created_at||"").slice(0,10)}</span></span> },
                    ]}/>
                  </div>
                </Card>
              )}
            </div>
          );
        })()}
      </Modal>

      {/* iter310 — Edit modal (admin/sysauth only) */}
      <Modal open={!!editOpen} onClose={() => setEditOpen(null)} title="Kayıt Düzenle" size="lg">
        {editOpen && (
          <div className="space-y-3">
            <div className="p-2 bg-amber-50 border border-amber-300 rounded text-[11px] text-amber-900">
              <b>Uyarı:</b> Bu kayıt {EVENT_KINDS.find(x=>x.key===editOpen.kind)?.label} türünde. Değişiklikler audit log'una kaydedilir.
            </div>
            <Input label="Kayıt Tarihi" type="date" value={editOpen.date} onChange={e => setEditOpen({ ...editOpen, date: e.target.value })} data-testid="gen-edit-date"/>
            {editOpen.kind === "pest" && <>
              <Input label="İlaçlama Firma" value={editOpen.payload?.company || ""} onChange={e => setEditOpen({ ...editOpen, payload: { ...editOpen.payload, company: e.target.value } })}/>
              <Input label="Belge No" value={editOpen.payload?.doc_no || ""} onChange={e => setEditOpen({ ...editOpen, payload: { ...editOpen.payload, doc_no: e.target.value } })}/>
              <Input label="İlaçlama Tarihi" type="date" value={editOpen.payload?.treatment_date || ""} onChange={e => setEditOpen({ ...editOpen, payload: { ...editOpen.payload, treatment_date: e.target.value } })}/>
              <Input label="Sonraki Uygulama" type="date" value={editOpen.payload?.next_due || ""} onChange={e => setEditOpen({ ...editOpen, payload: { ...editOpen.payload, next_due: e.target.value } })} data-testid="gen-edit-next-due"/>
            </>}
            {editOpen.kind === "training" && <>
              <Input label="Eğitim Adı" value={editOpen.payload?.training_name || ""} onChange={e => setEditOpen({ ...editOpen, payload: { ...editOpen.payload, training_name: e.target.value } })}/>
              <Input label="Eğitim Veren" value={editOpen.payload?.trainer || ""} onChange={e => setEditOpen({ ...editOpen, payload: { ...editOpen.payload, trainer: e.target.value } })}/>
              <Input label="Tekrar Eğitim Tarihi" type="date" value={editOpen.payload?.next_due || ""} onChange={e => setEditOpen({ ...editOpen, payload: { ...editOpen.payload, next_due: e.target.value } })} data-testid="gen-edit-next-due"/>
            </>}
            <Textarea label="Not" value={editOpen.payload?.notes || ""} onChange={e => setEditOpen({ ...editOpen, payload: { ...editOpen.payload, notes: e.target.value } })}/>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Yeni Belge Ekle</label>
              <MultiFileDropzone
                files={editOpen.payload?.files || []}
                onFilesAdded={fs => addFilesTo("edit", fs)}
                onRemove={i => removeFileFrom("edit", i)}
                testid="gen-edit-file"
                compact
              />
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setEditOpen(null)}>Vazgeç</Btn><Btn onClick={saveEdit} data-testid="gen-edit-save">Güncelle</Btn></div>
      </Modal>

      {/* PEST — enhanced with files + next_due */}
      <Modal open={open === "pest"} onClose={() => setOpen(null)} title="Haşere Takip ve İlaçlama Kaydı" size="md">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Kayıt Tarihi *" type="date" value={pest.date} onChange={e => setPest({ ...pest, date: e.target.value })}/>
          <Input label="İlaçlama Tarihi" type="date" value={pest.treatment_date} onChange={e => setPest({ ...pest, treatment_date: e.target.value })}/>
          <div className="col-span-2"><Input label="İlaçlama Firma Adı *" value={pest.company} onChange={e => setPest({ ...pest, company: e.target.value })} data-testid="pest-company"/></div>
          <div className="col-span-2"><Input label="İlaçlama Belge Numarası" value={pest.doc_no} onChange={e => setPest({ ...pest, doc_no: e.target.value })} data-testid="pest-doc"/></div>
          <Input label="Lokasyon / Alan" value={pest.location} onChange={e => setPest({ ...pest, location: e.target.value })} placeholder="Üretim Alanı, Depo..." data-testid="pest-location"/>
          <Input label="Sonraki Uygulama" type="date" value={pest.next_due} onChange={e => setPest({ ...pest, next_due: e.target.value })} data-testid="pest-next-due"/>
          <div className="col-span-2"><Textarea label="Not" value={pest.notes} onChange={e => setPest({ ...pest, notes: e.target.value })}/></div>
          <div className="col-span-2">
            <label className="text-xs font-semibold text-slate-600 block mb-1">Belge / Görsel (ilaçlama raporu, fatura, sertifika)</label>
            <MultiFileDropzone
              files={pest.files || []}
              onFilesAdded={fs => addFilesTo("pest", fs)}
              onRemove={i => removeFileFrom("pest", i)}
              label="Haşere belgelerini sürükle-bırak veya tıklayarak seç"
              testid="pest-file"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setOpen(null)}>Vazgeç</Btn><Btn onClick={savePest} data-testid="pest-save">Onayla ve Kaydet</Btn></div>
      </Modal>

      {/* WATER */}
      <Modal open={open === "water"} onClose={() => setOpen(null)} title="Saf Su Kontrolü Kaydı" size="lg">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Kayıt Tarihi *" type="date" value={water.date} onChange={e => setWater({ ...water, date: e.target.value })}/>
          <div/>
          <Input label="TDS Değeri" value={water.tds} onChange={e => setWater({ ...water, tds: e.target.value })} data-testid="water-tds"/>
          <Input label="pH Değeri" value={water.ph} onChange={e => setWater({ ...water, ph: e.target.value })} data-testid="water-ph"/>
          <Input label="Koku" value={water.odor} onChange={e => setWater({ ...water, odor: e.target.value })} data-testid="water-odor"/>
          <Input label="Görünüm" value={water.appearance} onChange={e => setWater({ ...water, appearance: e.target.value })} data-testid="water-appearance"/>
        </div>
        <div className="mt-3 border border-slate-200 rounded-lg p-3 bg-cyan-50/40">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold">Ek Parametreler</div>
            <Btn size="sm" variant="secondary" onClick={() => setWater({ ...water, extras: [...(water.extras || []), { name: "", value: "" }] })} data-testid="water-add-extra"><Plus size={12}/> Parametre Ekle</Btn>
          </div>
          {(water.extras || []).map((x, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center mb-2 bg-white border rounded p-1">
              <input value={x.name} onChange={e => { const arr = [...water.extras]; arr[i] = { ...arr[i], name: e.target.value }; setWater({ ...water, extras: arr }); }} placeholder="Parametre adı (İletkenlik, Sıcaklık...)" className="col-span-5 px-2 py-1 text-xs border rounded" data-testid={`water-ex-name-${i}`}/>
              <input value={x.value} onChange={e => { const arr = [...water.extras]; arr[i] = { ...arr[i], value: e.target.value }; setWater({ ...water, extras: arr }); }} placeholder="Değer" className="col-span-6 px-2 py-1 text-xs border rounded" data-testid={`water-ex-val-${i}`}/>
              <button onClick={() => setWater({ ...water, extras: water.extras.filter((_, j) => j !== i) })} className="col-span-1 text-red-500"><X size={14}/></button>
            </div>
          ))}
          {(water.extras || []).length === 0 && <div className="text-xs text-slate-400 italic">Ek parametre yok. "Parametre Ekle" ile İletkenlik, sıcaklık vs. ekleyebilirsiniz.</div>}
        </div>
        <div className="mt-3"><Textarea label="Not" value={water.notes} onChange={e => setWater({ ...water, notes: e.target.value })}/></div>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setOpen(null)}>Vazgeç</Btn><Btn onClick={saveWater} data-testid="water-save">Onayla ve Kaydet</Btn></div>
      </Modal>

      {/* TRAINING — enhanced with files + participants + next_due */}
      <Modal open={open === "training"} onClose={() => setOpen(null)} title="Eğitim Takip Kaydı" size="lg">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Tarih *" type="date" value={train.date} onChange={e => setTrain({ ...train, date: e.target.value })}/>
          <Input label="Tekrar Eğitim Tarihi" type="date" value={train.next_due} onChange={e => setTrain({ ...train, next_due: e.target.value })} data-testid="train-next-due"/>
          <div className="col-span-2"><Input label="Eğitim Adı *" value={train.training_name} onChange={e => setTrain({ ...train, training_name: e.target.value })} data-testid="train-name"/></div>
          <Input label="Personel Adı (metin, ops.)" value={train.staff_name} onChange={e => setTrain({ ...train, staff_name: e.target.value })} data-testid="train-staff"/>
          <Input label="Eğitim Veren" value={train.trainer} onChange={e => setTrain({ ...train, trainer: e.target.value })} data-testid="train-trainer"/>
          <div className="col-span-2">
            <label className="text-xs font-semibold text-slate-600">Katılımcı Personel ({employees.length} personelden seçin)</label>
            <select multiple size={5} value={train.participant_ids} onChange={e => setTrain({ ...train, participant_ids: Array.from(e.target.selectedOptions).map(o => o.value) })} className="w-full mt-1 text-xs border rounded px-2 py-1" data-testid="train-participants">
              {employees.map(em => <option key={em.id} value={em.id}>{em.full_name || em.name || em.id}</option>)}
            </select>
            <div className="text-[11px] text-slate-400 mt-1">Ctrl / Cmd basılı tutarak birden fazla seçebilirsiniz. Seçili: {train.participant_ids.length}</div>
          </div>
          <div className="col-span-2"><Input label="Eğitime Katılan İmza (metin)" value={train.signature} onChange={e => setTrain({ ...train, signature: e.target.value })} data-testid="train-signature"/></div>
          <div className="col-span-2"><Textarea label="Not" value={train.notes} onChange={e => setTrain({ ...train, notes: e.target.value })}/></div>
          <div className="col-span-2">
            <label className="text-xs font-semibold text-slate-600 block mb-1">Belge / Görsel (sunum, imzalı katılım formu, sertifika, foto)</label>
            <MultiFileDropzone
              files={train.files || []}
              onFilesAdded={fs => addFilesTo("train", fs)}
              onRemove={i => removeFileFrom("train", i)}
              label="Eğitim belgelerini sürükle-bırak veya tıklayarak seç"
              testid="train-file"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setOpen(null)}>Vazgeç</Btn><Btn onClick={saveTrain} data-testid="train-save">Onayla ve Kaydet</Btn></div>
      </Modal>
    </div>
  );
}

/* =================== PAGE =================== */
export function GMPPage() {
  const [tab, setTab] = useState("cleaning");
  const tabs = [
    { key: "temp", label: "Sıcaklık & Nem", icon: Thermometer, color: "cyan" },
    { key: "cleaning", label: "Temizlik & Hijyen", icon: Sparkles, color: "emerald" },
    { key: "maintenance", label: "Bakım & Kalibrasyon", icon: Wrench, color: "amber" },
    { key: "documents", label: "Doküman & SOP", icon: FileText, color: "indigo" },
    { key: "master_documents", label: "Master Doküman Listesi", icon: BookMarked, color: "indigo" },
    { key: "suppliers", label: "Tedarikçi Değerlendirme", icon: Users2, color: "rose" },
    { key: "retained", label: "Şahit Numune", icon: FlaskConical, color: "purple" },
    // — Olaylar
    { key: "capa", label: "CAPA", icon: Shield, color: "amber" },
    { key: "deviations", label: "Sapma", icon: AlertTriangle, color: "orange" },
    { key: "complaints", label: "Şikayet", icon: MessageCircleWarning, color: "rose" },
    { key: "oos", label: "OOS", icon: FlaskConical, color: "red" },
    // — Yönetim
    { key: "change_control", label: "Değişiklik Kontrolü", icon: GitBranch, color: "sky" },
    { key: "risk", label: "Risk Yönetimi", icon: AlertOctagon, color: "amber" },
    // — Denetim
    { key: "audit", label: "İç Denetim", icon: ClipboardCheck, color: "indigo" },
    // — Diğer
    { key: "recall", label: "Geri Çağırma", icon: Undo2, color: "rose" },
    { key: "waste", label: "Atık Takibi", icon: Recycle, color: "emerald" },
    { key: "general", label: "Genel Takip", icon: ClipboardList, color: "violet" },
    { key: "fason", label: "Fason İmalat", icon: FileText, color: "teal" },
  ];
  return (
    <div>
      <PageHeader title="GMP Portalı" description="İyi Üretim Uygulamaları — Temizlik, Ortam, Bakım/Kalibrasyon, SOP, Tedarikçi, Şahit Numune" />
      <GMPAlertsBanner onGoto={(t) => setTab(t)}/>
      <div className="flex flex-wrap gap-1 mb-4 border-b border-slate-200 pb-0" data-testid="gmp-tabs">
        {tabs.map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`gmp-tab-${t.key}`}
              className={`px-3 md:px-4 py-2 text-xs md:text-sm font-semibold flex items-center gap-1.5 md:gap-2 border-b-2 -mb-px transition min-h-[40px] ${active ? `border-${t.color}-500 text-${t.color}-700 bg-${t.color}-50/50` : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"}`}>
              <Icon size={16}/> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "cleaning" && <CleaningTab/>}
      {tab === "temp" && <TempHumidityTab/>}
      {tab === "maintenance" && <MaintenanceTab/>}
      {tab === "documents" && <DocumentsTab/>}
      {tab === "master_documents" && <MasterDocuments/>}
      {tab === "suppliers" && <SupplierEvalTab/>}
      {tab === "retained" && <RetainedSamplesTab/>}
      {tab === "capa" && <CAPAPanel testidPrefix="gmp-capa"/>}
      {tab === "deviations" && <DeviationsTab/>}
      {tab === "complaints" && <ComplaintsTab/>}
      {tab === "oos" && <OOSTab/>}
      {tab === "change_control" && <ChangeControlTab/>}
      {tab === "risk" && <RiskTab/>}
      {tab === "audit" && <InternalAuditTab/>}
      {tab === "recall" && <RecallSummaryTab/>}
      {tab === "waste" && <WasteTab/>}
      {tab === "general" && <GeneralTab/>}
      {tab === "fason" && <FasonContractsTab/>}
    </div>
  );
}
